import React, { FC, useEffect, useState } from 'react';
import { Button, IconButton, Paper, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getRoute } from 'src/routes';
import { AccrualIcon, EmptyState, InfoFilledIcon, ServerError500Icon, Tab, Tabs, Tooltip } from 'src/shared/components';
import { ComponentWithFeatures } from 'src/shared/hocs';
import { useThunkDispatch } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { fetchAbsenceBalances } from 'src/store/absenceBalance/absenceBalance.actions';
import { RootState } from 'src/store/types';

import { FeaturesEnums } from '../../../shared/enums';

import { AccrualBalancesMetrics } from './AccrualBalancesMetrics';

export const AccrualBalancesCard: FC = () => {
  const { t } = useTranslate();
  const dispatch = useThunkDispatch();
  const navigate = useNavigate();
  const { data: balances, loading, error } = useSelector((state: RootState) => state.absenceBalances);
  const { settings } = useSelector((state: RootState) => state.user);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    dispatch(fetchAbsenceBalances());
  }, []);

  const handleTabChange = (index: number) => {
    setCurrentIndex(index);
  };

  const handleNavigateToBalanceDetails = () => {
    navigate(getRoute('balances'));
  };

  if (loading) {
    return <Skeleton className="rounded border" variant="rectangular" animation="wave" height={320} />;
  }

  if (error) {
    return (
      <Paper className="h-100 p-4">
        <h4 className="pb-0 mb-0">{t('absences.absenceBalances')}</h4>
        <EmptyState className="m-0" message={t('global.oopsAnErrorOccurred')} icon={<ServerError500Icon />} />
      </Paper>
    );
  }

  return (
    <Paper id="accrual-balances-card" className="h-100 p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2">
        <div className="d-flex align-items-center">
          <h4 className="pb-0 mb-0 me-1">{t('absences.absenceBalances')}</h4>
          <Tooltip placement="top" title={t('absences.summaryOfYourTimeOffBalances')}>
            <IconButton className="p-0" size="small" color="info">
              <InfoFilledIcon size={16} />
            </IconButton>
          </Tooltip>
        </div>

        <ComponentWithFeatures features={[FeaturesEnums.AccrualSummary, FeaturesEnums.AccrualsFunction]} strict>
          <Button
            id="view-balance-details"
            startIcon={<AccrualIcon size={20} />}
            variant="contained"
            color="secondary"
            onClick={handleNavigateToBalanceDetails}
          >
            {t('accrualSummary.viewBalanceDetails')}
          </Button>
        </ComponentWithFeatures>
      </div>

      <div>
        <Tabs value={currentIndex} onClick={handleTabChange}>
          {balances.map((balance, index) => {
            return <Tab key={balance.benefitId} value={index} label={balance.benefitName} />;
          })}
        </Tabs>
      </div>
      <AccrualBalancesMetrics
        balance={balances[currentIndex]}
        shouldDisplayAsDays={!!settings?.showAccrualBalanceInDays}
      />
    </Paper>
  );
};
